import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Checkbox,
    Box
} from '@mui/material';
import { DateTime } from 'luxon';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { useTranslation } from "react-i18next";
import "../../css/components/Core/TaskDatesDialog.css";
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const TaskDatesDialog = ({ open, onClose, task, handleSaveDates, myUserTimezone }) => {
    const { t } = useTranslation();

    const handleOnClose = () => {
        setStartDate(null);
        setDueDate(null);
        setEndDate(null);
        setIsStartDateNull(null);
        setIsDueDateNull(null);
        setIsEndDateNull(null);
        onClose();
    }


    const [startDate, setStartDate] = useState(null);
    const [dueDate, setDueDate] = useState(null);
    const [endDate, setEndDate] = useState(null);

    const [isStartDateNull, setIsStartDateNull] = useState(true);
    const [isDueDateNull, setIsDueDateNull] = useState(true);
    const [isEndDateNull, setIsEndDateNull] = useState(true);

    useEffect(() => {
        // Update dates based on the task and timezone props
        if (task.start_date) {
            setStartDate(DateTime.fromISO(task.start_date).setZone(myUserTimezone));
            setIsStartDateNull(false);
        } else {
            setStartDate(null);
            setIsStartDateNull(true);
        }

        if (task.due_date) {
            setDueDate(DateTime.fromISO(task.due_date).setZone(myUserTimezone));
            setIsDueDateNull(false);
        } else {
            setDueDate(null);
            setIsDueDateNull(true);
        }

        if (task.end_date) {
            setEndDate(DateTime.fromISO(task.end_date).setZone(myUserTimezone));
            setIsEndDateNull(false);
        } else {
            setEndDate(null);
            setIsEndDateNull(true);
        }
    }, [task, open]);

    const handleSave = () => {
        handleSaveDates({
            start_date: isStartDateNull ? null : startDate.toUTC().toISO(),
            due_date: isDueDateNull ? null : dueDate.toUTC().toISO(),
            end_date: isEndDateNull ? null : endDate.toUTC().toISO()
        });

    };

    const changeStartDate = (isChecked) => {
        if (!isChecked) {
            setStartDate(DateTime.now().setZone(myUserTimezone));
        }
        setIsStartDateNull(isChecked);
    };

    const changeDueDate = (isChecked) => {
        if (!isChecked) {
            setDueDate(DateTime.now().setZone(myUserTimezone));
        }
        setIsDueDateNull(isChecked);
    };

    const changeEndDate = (isChecked) => {
        if (!isChecked) {
            setEndDate(DateTime.now().setZone(myUserTimezone));
        }
        setIsEndDateNull(isChecked);
    };



    return (
        <Dialog
            open={open}
            onClose={handleOnClose}
            className="task-dates-main-dialog"
        >
            <DialogTitle className="task-dates-dialog-title">{t("core.dates")}</DialogTitle>
            <DialogContent>
                <LocalizationProvider dateAdapter={AdapterLuxon}>
                    <Box className="task-dates-box">
                        {/* Start Date */}
                        <Box className="task-dates-row">
                            <Checkbox
                                checked={isStartDateNull}
                                onChange={(e) => changeStartDate(e.target.checked)}
                                className="task-dates-checkbox-control"
                            />
                            <Typography className="task-dates-checkbox-label">
                                {t("core.no_start_date")}
                            </Typography>
                        </Box>

                        <DateTimePicker
                            label={t("global.start_date")}
                            value={isStartDateNull ? null : startDate}
                            onChange={(newValue) => setStartDate(newValue.setZone(myUserTimezone))}
                            className="task-dates-datetime-picker"
                            disabled={isStartDateNull}
                        />

                        {/* Due Date */}
                        <Box className="task-dates-row">
                            <Checkbox
                                checked={isDueDateNull}
                                onChange={(e) => changeDueDate(e.target.checked)}
                                className="task-dates-checkbox-control"
                            />
                            <Typography className="task-dates-checkbox-label">
                                {t("core.no_due_date")}
                            </Typography>
                        </Box>

                        <DateTimePicker
                            label={t("core.due_date")}
                            value={isDueDateNull ? null : dueDate}
                            onChange={(newValue) => setDueDate(newValue.setZone(myUserTimezone))}
                            className="task-dates-datetime-picker"
                            disabled={isDueDateNull}
                        />

                        {/* End Date */}
                        <Box className="task-dates-row">
                            <Checkbox
                                checked={isEndDateNull}
                                onChange={(e) => changeEndDate(e.target.checked)}
                                className="task-dates-checkbox-control"
                            />
                            <Typography className="task-dates-checkbox-label">
                                {t("core.no_end_date")}
                            </Typography>
                        </Box>

                        <DateTimePicker
                            label={t("calendar.event_end_date")}
                            value={isEndDateNull ? null : endDate}
                            onChange={(newValue) => setEndDate(newValue.setZone(myUserTimezone))}
                            className="task-dates-datetime-picker"
                            disabled={isEndDateNull}
                        />
                    </Box>
                </LocalizationProvider>
            </DialogContent>

            <DialogActions className="task-dates-dialog-actions">
                <Button onClick={handleOnClose} className="decline-button">
                    <CancelIcon style={{ marginRight: 4 }} />
                    {t("global.cancel")}
                </Button>
                <Button onClick={handleSave} className="accept-button">
                    <CheckCircleIcon style={{ marginRight: 4 }} />
                    {t("modal.confirm")}
                </Button>

            </DialogActions>
        </Dialog>
    );
};

export default TaskDatesDialog;
